import React, { useId } from 'react'
import PropTypes from 'prop-types'
import { Checkbox } from 'primereact/checkbox'
import './WingsCheckbox.css'

/**
 * WingsCheckbox Component
 *
 * A styled checkbox built on PrimeReact's Checkbox, matching the Wingspans
 * design system. Supports individual use and group composition via the
 * shared `name` prop.
 *
 * @component
 * @example
 * // Standalone
 * <WingsCheckbox
 *   label="Add to action plan"
 *   checked={linked}
 *   onChange={(checked) => setLinked(checked)}
 * />
 *
 * @example
 * // JSX label content
 * <WingsCheckbox
 *   label={<FormattedMessage id="dashboard-filter-complete-goal" defaultMessage="Completed" />}
 *   checked={completed}
 *   onChange={setCompleted}
 * />
 *
 * @example
 * // Group of checkboxes
 * <WingsCheckboxGroup legend="Interests" helpText="Select all that apply">
 *   <WingsCheckbox
 *     label="Technology"
 *     name="interests"
 *     value="technology"
 *     checked={selected.includes('technology')}
 *     onChange={(checked) => toggleInterest('technology', checked)}
 *   />
 *   <WingsCheckbox
 *     label="Healthcare"
 *     name="interests"
 *     value="healthcare"
 *     checked={selected.includes('healthcare')}
 *     onChange={(checked) => toggleInterest('healthcare', checked)}
 *   />
 * </WingsCheckboxGroup>
 *
 * @example
 * // Colored checkboxes
 * <WingsCheckbox label="Teal" color="teal" checked={...} onChange={...} />
 * <WingsCheckbox label="Blue" color="blue" checked={...} onChange={...} />
 * <WingsCheckbox label="Pink" color="pink" checked={...} onChange={...} />
 */
const WingsCheckbox = ({
  label,
  labelPosition = 'right',
  id,
  name,
  value,
  checked = false,
  onChange,
  disabled = false,
  error = false,
  color = 'gray',
  className = '',
  ...props
}) => {
  const generatedId = useId()
  const inputId = id || (typeof label === 'string' && label ? `checkbox-${label.toLowerCase().replace(/\s+/g, '-')}` : `checkbox-${generatedId.replace(/:/g, '')}`)

  const handleChange = (e) => {
    onChange?.(e.checked)
  }

  const wrapperClasses = [
    'wings-checkbox-wrapper',
    disabled && 'wings-checkbox-wrapper--disabled',
    error && 'wings-checkbox-wrapper--error',
    color && `wings-checkbox-wrapper--${color}`,
    className,
  ].filter(Boolean).join(' ')

  const labelElement = label ? (
    <label className="wings-checkbox-label" htmlFor={inputId}>
      {label}
    </label>
  ) : null

  return (
    <div className={wrapperClasses}>
      {labelPosition === 'left' && labelElement}
      <Checkbox
        inputId={inputId}
        name={name}
        value={value}
        checked={checked}
        onChange={handleChange}
        disabled={disabled}
        className="wings-checkbox"
        {...props}
      />
      {labelPosition !== 'left' && labelElement}
    </div>
  )
}

WingsCheckbox.propTypes = {
  /** Label displayed next to the checkbox */
  label: PropTypes.node,
  /** Position of the label relative to the checkbox */
  labelPosition: PropTypes.oneOf(['left', 'right']),
  /** HTML id for the input; auto-generated from label if omitted */
  id: PropTypes.string,
  /** Name attribute — used to group related checkboxes */
  name: PropTypes.string,
  /** Value this checkbox represents in a group */
  value: PropTypes.any,
  /** Whether the checkbox is checked */
  checked: PropTypes.bool,
  /** Callback fired on change; receives the new boolean checked state */
  onChange: PropTypes.func,
  /** Disables the checkbox */
  disabled: PropTypes.bool,
  /** Applies error styling */
  error: PropTypes.bool,
  /** Color theme for the checkbox */
  color: PropTypes.oneOf(['teal', 'blue', 'purple', 'pink', 'orange', 'yellow']),
  /** Additional CSS classes applied to the wrapper element */
  className: PropTypes.string,
}

export default WingsCheckbox

/**
 * WingsCheckboxGroup Component
 *
 * A fieldset wrapper for grouping multiple WingsCheckbox components with a
 * shared legend, optional helper text, and error message.
 *
 * @component
 * @example
 * <WingsCheckboxGroup legend="Notification preferences" error={formErrors.notifications}>
 *   <WingsCheckbox label="Email" name="notify" value="email" checked={...} onChange={...} />
 *   <WingsCheckbox label="SMS" name="notify" value="sms" checked={...} onChange={...} />
 * </WingsCheckboxGroup>
 */
export const WingsCheckboxGroup = ({
  legend,
  helpText,
  error,
  children,
  className = '',
}) => {
  const groupClasses = [
    'wings-checkbox-group',
    error && 'wings-checkbox-group--error',
    className,
  ].filter(Boolean).join(' ')

  return (
    <fieldset className={groupClasses}>
      {legend && <legend className="wings-checkbox-group-legend">{legend}</legend>}
      <div className="wings-checkbox-group-items">{children}</div>
      {(helpText || error) && (
        <span className={`wings-checkbox-group-hint${error ? ' wings-checkbox-group-hint--error' : ''}`}>
          {error || helpText}
        </span>
      )}
    </fieldset>
  )
}

WingsCheckboxGroup.propTypes = {
  /** Group label rendered as a fieldset legend */
  legend: PropTypes.string,
  /** Helper text displayed below the group */
  helpText: PropTypes.string,
  /** Error message displayed below the group */
  error: PropTypes.string,
  /** WingsCheckbox children */
  children: PropTypes.node.isRequired,
  /** Additional CSS classes applied to the fieldset */
  className: PropTypes.string,
}
